import { FC, lazy } from 'react';
import { Heading } from 'src/components/heading';
import { PageSpinner } from 'src/components/spinner-container';
import { Stack } from 'src/components/stack';
import { useMarketAreas } from 'src/pages/reporting/my-transactions/use-market-areas';
import { useCapacityCategories } from 'src/pages/reporting/products/drafted/overview/use-capacity-categories';
import { usePageParams } from 'src/pages/reporting/shipper-transactions/use-page-params';
import { useShipperTransactions } from 'src/pages/reporting/shipper-transactions/use-shipper-transactions';
import { useCapacityManagementRoot } from 'src/pages/transport/network-points/details/availability-and-price-data/overview/use-capacity-management-root';
import { ShipperTransactionsParams } from 'src/types/capacity-management';
import { useMemoOne } from 'use-memo-one';

const TransactionsPage = lazy(() => import('./page'));

export const ShipperTransactions: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const capacityCategories = useCapacityCategories({ capacityManagementRoot });
  const marketAreas = useMarketAreas({ capacityManagementRoot });

  const pageParams = usePageParams();

  const params = useMemoOne<ShipperTransactionsParams>(() => {
    const {
      start,
      pageSize,
      networkPointId,
      shipperId,
      transactionTypeSpecificId,
      ...rest
    } = pageParams.value;
    return {
      offset: start,
      limit: pageSize,
      networkPointId: networkPointId.map((id) => id.value),
      shipperId: shipperId.map((id) => id.value),
      transactionTypeSpecificId: transactionTypeSpecificId
        ? [transactionTypeSpecificId]
        : [],
      ...rest,
    };
  }, [pageParams.value]);

  const shipperTransactions = useShipperTransactions({
    capacityManagementRoot,
    params,
  });

  if (!capacityManagementRoot.response) return <PageSpinner />;

  return (
    <Stack gap={2}>
      <Heading mode="title">Shipper Transactions</Heading>

      <TransactionsPage
        capacityManagementRoot={capacityManagementRoot}
        shipperTransactions={shipperTransactions}
        pageParams={pageParams}
        capacityCategories={capacityCategories}
        marketAreas={marketAreas}
      />
    </Stack>
  );
};
